import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map} from 'rxjs/operators';
import { AppSettingService } from '../app.service';
import { CacheService } from '../utility/cache.service';
import { UserInfoService } from '../user/user-info.service';

//this should be 1:1 with MAP API's .net SaveMapType
interface ISaveMapType {
		CareerVersion: string,
		CertificationVersion: string,
		CTEVersion: string,
		MilitaryVersion: string,
		CollegeVersion: string,
		K12Version: string,
		PreKVersion: string
}

export interface ISaveUserMapOption {
	MapOptionId: string,
	Territories: any
}

export interface ITerritoryIPED {
	CatalogueLabel: string,
	IpedsUnitId: string,
	IsUsed: boolean,
	Name: string,
	NonIpedsInstitutionId: string,
	SelectedCollegeBaseName: string,
	Version: string,
	XLoc: number,
	YLoc: number
}

export interface IMapOptionCollege {
	CollegeId: string,
	Name: string
}

export interface IMapOptionIPEDTrritories {
	CTEVersion: ITerritoryIPED,
	CTEVersions: ITerritoryIPED[],
	CareerVersion: ITerritoryIPED,
	CareerVersions: ITerritoryIPED[]
	CertificationVersion: ITerritoryIPED,
	CertificationVersions: ITerritoryIPED[],
	CollegeVersion: ITerritoryIPED,
	CollegeVersions: ITerritoryIPED[],
	K12Version: ITerritoryIPED,
	K12Versions: ITerritoryIPED[],
	MilitaryVersion: ITerritoryIPED,
	MilitaryVersions: ITerritoryIPED[],
	PreKVersion: ITerritoryIPED,
	PreKVersions: ITerritoryIPED[]
}

export interface IMapOptionIPED {
	MapOptionId: string,
	MapOptionName: string,
	MapShapeId: string,
	PolygonTotal: number,
	IsDefault: boolean,
	Hide: boolean,
	InProcess: boolean,
	Territories: IMapOptionIPEDTrritories,
	CollegeDistinctVersions: IMapOptionCollege[]
}

export interface IMapOptionTerritory {
	TerritoryId: number,
	Name: string, //catalog name
	Version: string
	IsAvailable: boolean,
	IsDefault: boolean,
	IsUsed: boolean
	PolygonPosition: number,
	MapName: string,
	K8Name: string,
	HSName: string,
}

export interface IMapOption {
	MapOptionId: string,
	MapOptionName: string,
	MapShapeId: string,
	UnlockPolygon: boolean,
	PolygonTotal: number,
	IsDefault: boolean,
	IsUsed: boolean,
	Hide: boolean,
	InProcess: boolean,
	CareerVersions: IMapOptionTerritory[],
	CertificationVersions: IMapOptionTerritory[],
	CTEVersions: IMapOptionTerritory[],
	MilitaryVersions: IMapOptionTerritory[],	
	CollegeVersions: IMapOptionTerritory[],
	K12Versions: IMapOptionTerritory[],
	PreKVersions: IMapOptionTerritory[]
}

@Injectable({
	providedIn: 'root'
})
export class MapAPIService {
	constructor(
		private cacheSrv: CacheService,
		private http: HttpClient,
		private userInfo: UserInfoService		
	) { }

	//used for alternative map demo
	alternativeMapDemoId: any = "isAlternativeMapDemoId";

	//private url = AppSettingService.appConfig.informAPIUrl + "MAP";
	private url = AppSettingService.appConfig.JourneyAPIUrl + "Map"; //API2

	private getBlankSaveMapType(): ISaveMapType {
		return {
			CareerVersion: null,
			CertificationVersion: null,
			CTEVersion: null,
			MilitaryVersion: null,
			CollegeVersion: null,
			K12Version: null,
			PreKVersion: null
		}
	}
	
	commaDelimitedUserMapVersions: string = "";
	
	getFilledInSaveMapType(userMapResponseObject: any): ISaveMapType {
		let saveMapTypeObj: any = this.getBlankSaveMapType();
		let saveMapTypeKeys: any = Object.keys(saveMapTypeObj);

		for (var i: any = 0; i < saveMapTypeKeys.length; i++) {
			//this works because userMapResponse object has same keys as saveMapType
			//so we "fill in" the value for each saveMapType key with the corresponding userMapResponse object key's value
			saveMapTypeObj[saveMapTypeKeys[i]] = userMapResponseObject[saveMapTypeKeys[i]]['Version'];
		}

		return saveMapTypeObj;
	}
	
	getUserMapSavePostData(getUserMapResponse: any): ISaveUserMapOption {
		let postData: any = {
			MapOptionId: null,
			Territories: {
			}
		};
		postData.MapOptionId = getUserMapResponse.MapOptionId;
		if (getUserMapResponse.PreKVersion) { postData.Territories.PreKVersion = getUserMapResponse.PreKVersion.Version; }
		if (getUserMapResponse.K12Version) { postData.Territories.K12Version = getUserMapResponse.K12Version.Version; }
		if (getUserMapResponse.CollegeVersion) { postData.Territories.CollegeVersion = getUserMapResponse.CollegeVersion.Version; }
		if (getUserMapResponse.CareerVersion) { postData.Territories.CareerVersion = getUserMapResponse.CareerVersion.Version; }
		if (getUserMapResponse.MilitaryVersion) { postData.Territories.MilitaryVersion = getUserMapResponse.MilitaryVersion.Version; }
		if (getUserMapResponse.CTEVersion) { postData.Territories.CTEVersion = getUserMapResponse.CTEVersion.Version; }
		if (getUserMapResponse.CertificationVersion) { postData.Territories.CertificationVersion = getUserMapResponse.CertificationVersion.Version; }
		return postData;
	}

	viewMap(minX: number, maxX: number, minY: number, maxY: number, zoom: number, versions: string, selectedObjectId: string, mapOptionId: string = 'default', clipMinX: number = null, clipMinY: number = null, clipMaxX: number = null, clipMaxY: number = null): Rx.Observable<any> {		
		if (clipMinX == null || clipMinY == null || clipMaxX == null || clipMaxY == null) {
			clipMinX = -1;
			clipMinY = -1;
			clipMaxX = -1;
			clipMaxY = -1;
		}

		return this.http.get<any>(
			this.url + "/Option/View/" + mapOptionId + "?minX=" + minX.toFixed(4) + "&maxX=" + maxX.toFixed(4) + "&minY=" + minY.toFixed(4) + "&maxY=" + maxY.toFixed(4) + "&zoom=" + zoom.toFixed(4) + "&mvs=" + encodeURIComponent(versions) + "&so=" + encodeURIComponent(selectedObjectId) + "&clipMinX=" + encodeURIComponent(clipMinX) + "&clipMinY=" + encodeURIComponent(clipMinY) + "&clipMaxX=" + encodeURIComponent(clipMaxX) + "&clipMaxY=" + encodeURIComponent(clipMaxY),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getMapObject(objectId: string, mapOptionId: string = 'default'): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Object/" + encodeURIComponent(objectId) + "?mapOptionId=" + mapOptionId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getMapObjectAncestory(objectId: string): Rx.Observable<any> {
		let l = this.url + "/Ancestory/" + encodeURIComponent(objectId);
		return this.cacheSrv.get(true, l);
	}
	
	getSolrMapSearch(mapOptionId: string = 'default', keyword: string, limit: number, offset: number, searchOnlyMapVersion: string, myCareerfilterObject: any): Rx.Observable<any> {
		const myCareerfilterObjectStringified = Object.keys(myCareerfilterObject).map((key: any) => key + '=' + myCareerfilterObject[key+'']).join('&');
		return this.http.get<any>(
			this.url + '/MapSearch/' + mapOptionId + '?keyword=' + encodeURIComponent(keyword) + '&limit=' + limit + '&offset=' + offset + '&searchOnlyTerritory=' + searchOnlyMapVersion + '&' + myCareerfilterObjectStringified,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);		
	}

	getMapObjectStatus(id: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Search/Status/" + id,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getClientMap(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Client",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getDemoPermission(userGuid: string): Rx.Observable<boolean> {
		return this.http.get<any>(
			this.url + "/DemoPermission/" + encodeURIComponent(userGuid),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		)
	}


	private userMapCache:any = null;

	clearMapCache(): void {
		this.userMapCache = null;
	}

	getUserMap(reload: boolean = false, mapOptionId: string = 'default'): Rx.Observable<any> {		
		/*
		reload = reload == null ? false : reload;
		
		if (reload) {
			this.clearMapCache();
		}
		*/
		reload = true; //need to make cache work with get all map versions (when mapOptionId is null)
		if (!reload && this.userMapCache != null) {
			return Rx.of(this.userMapCache);
		}
		else {
			//null value for _default returns available map options: 
			//'default' returns default map for user, 
			//other values return maps for given value.
			let restOfUrl = "/Option/User/" + mapOptionId;
			if (mapOptionId === null) {
				restOfUrl = "/Option/User";
			}

			return this.http.get<any>(
				this.url + restOfUrl,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				tap(
					response => {
						this.userMapCache = response;
					}
				)
			);
		}
	}
	
	convertToList(getUserMapResponse: any): string {
		let vls: string[] = [];
		Object.keys(getUserMapResponse).forEach((key) => {
			// collect the data in Version not in Versions
			if (key.split("").reverse().join("").indexOf("s") > 0) {
				if (getUserMapResponse[key]) {
					vls.push(getUserMapResponse[key]["Version"]);
				}
			}
		});
		// sort and join the version to a list by territory id , such 1-1,2,1,3-1...
		let retStr = vls.sort().join(",");

		//remove trailing comma if exist
		if (retStr.length > 0 && retStr[retStr.length - 1] === ',') {
			retStr = retStr.slice(0, retStr.length - 1)
		}

		return retStr;
	};
	 
	getUserMapVersionList(reload: boolean = false, mapOptionId: string='default'): Rx.Observable<any> { // reutn selected map versions in comma list
		reload = reload == null ? false : reload;
		if (reload) {
			this.clearMapCache();
		}

		if (!reload && this.userMapCache != null) {
			return Rx.of(this.convertToList(this.userMapCache));
		}
		else {
			return this.http.get<any>(
				this.url + '/Option/User/'+mapOptionId,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				map(
					response => {
						this.userMapCache = response;
						return this.convertToList(response);
					}
				)
			);
		}
	}

	saveUserMap(mapSelection: ISaveUserMapOption): Rx.Observable<any> { 
		return this.http.post<any>(
			this.url + "/Option/User",
			mapSelection,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(
			tap(
				response => {
					this.clearMapCache();
				}
			)
		);
	}
	
	getCorrespondingMapObject(fromOccupationId: string): Rx.Observable<any> { 
		return this.http.get<any>(
			this.url + "/GetCorrespondingMapObject/"+ fromOccupationId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserCollegesByCip(cip:string, mappObjectId: string) : Rx.Observable<any> { 
		return this.http.get<any>(
			this.url + "/GetUserCollegesByCip/" + encodeURIComponent(cip) + "/" + encodeURIComponent(mappObjectId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	GetUserCollegesByOccupation(mapObjectId:string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GetUserCollegesByOccupation/" + encodeURIComponent(mapObjectId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	setCommaDelimitedUserMapVersions() {
		this.getUserMapVersionList().subscribe(
			(commaDelimitedUserMapVersions) => {
				this.commaDelimitedUserMapVersions = commaDelimitedUserMapVersions;
			});
	}

	//#region map option
	getUserMapOptions(limit: number, offset: number): Rx.Observable<IMapOptionIPED[]> {
		return this.http.get<IMapOptionIPED[]>(
			this.url + "/Option/User?limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	// when mapOptionId is not provided, get the default
	getUserMapOption(reload: boolean = false, mapOptionId: string = null): Rx.Observable<IMapOptionIPEDTrritories> {
		reload = reload == null ? false : reload;
		if (reload) {
			this.clearMapCache();
		}

		if (!reload && this.userMapCache != null) {
			return Rx.of(this.userMapCache);
		}
		else {
			return this.http.get<IMapOptionIPED>(
				this.url + "/Option/User/" + encodeURIComponent(mapOptionId==null?"default":mapOptionId),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				map(
					response => {
						this.userMapCache = response.Territories;
						return response.Territories;
					}
				)
			);
		}
	}

	getZoneMapOptions(limit: number, offset: number): Rx.Observable<IMapOption[]> {
		return this.http.get<IMapOption[]>(
			this.url + "/Option/Zone?limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZoneMapOption(mapOptionId: string, forNew: boolean, mapShapeId: number): Rx.Observable<IMapOption> {
		if (forNew) {
			return this.http.get<IMapOption>(
				this.url + "/Option/Zone/New/" + encodeURIComponent(mapShapeId),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		}
		else {
			return this.http.get<IMapOption>(
				this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId == null ? "default" : mapOptionId),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		}
	}


	toggleMapOptionHidden(mapOptionId: string, hide: boolean): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId) + "/Hide/" + hide.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	toggleMapOptionDefault(mapOptionId: string, isDefault: boolean): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId) + "/Default/" + isDefault.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveZoneMapOption(option: IMapOption): Rx.Observable<IMapOption> {
		return this.http.post<any>(
			this.url + "/Option/Zone",
			option,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteZoneMapOption(mapOptionId: string): Rx.Observable<any> {
		return this.http.delete<any>(
			this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getVersion(version: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Version/" + encodeURIComponent(version),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateVersion(version: string, name: string, hs: string, k8: string): Rx.Observable<any> {
		return this.http.put<any>(
			this.url + "/Version/" + version + "?mapName=" + encodeURIComponent(name) + "&hsName=" + encodeURIComponent(hs) + "&k8Name=" + encodeURIComponent(k8),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion map option
	getBlankMapTerritoryVersionObj(): {
		CTEVersion: null | string;
		CareerVersion: null | string;
		CertificationVersion: null | string;
		CollegeVersion: null | string;
		K12Version: null | string;
		MilitaryVersion: null | string;
		PreKVersion: null | string;
	} {
		return {
			CTEVersion: null,
			CareerVersion: null,
			CertificationVersion: null,
			CollegeVersion: null,
			K12Version: null,
			MilitaryVersion: null,
			PreKVersion: null
		};
	}

	getMapTerritoryVersionObjFromGetUserMapResponse(getUserMapResponse: any): any {
		let returnObj = this.getBlankMapTerritoryVersionObj();

		returnObj.CTEVersion = (getUserMapResponse.CTEVersion) ? getUserMapResponse.CTEVersion.Version : null;
		returnObj.CareerVersion = (getUserMapResponse.CareerVersion) ? getUserMapResponse.CareerVersion.Version : null;
		returnObj.CertificationVersion = (getUserMapResponse.CertificationVersion) ? getUserMapResponse.CertificationVersion.Version : null;
		returnObj.CollegeVersion = (getUserMapResponse.CollegeVersion) ? getUserMapResponse.CollegeVersion.Version : null;
		returnObj.K12Version = (getUserMapResponse.K12Version) ? getUserMapResponse.K12Version.Version : null;
		returnObj.MilitaryVersion = (getUserMapResponse.MilitaryVersion) ? getUserMapResponse.MilitaryVersion.Version : null;
		returnObj.PreKVersion = (getUserMapResponse.PreKVersion) ? getUserMapResponse.PreKVersion.Version : null;

		return returnObj;
	}

	getMapSavePostDataFromTerritoryVersionsObj(territoryVersionsObj: any, mapOptionId: string): any {
		var postObj: any = {
			MapOptionId: mapOptionId,
			Territories: {
				CTEVersion: null,
				CareerVersion: null,
				CertificationVersion: null,
				CollegeVersion: null,
				K12Version: null,
				MilitaryVersion: null,
				PreKVersion: null
			}
		};

		postObj.Territories.CTEVersion = (territoryVersionsObj.CTEVersion) ? territoryVersionsObj.CTEVersion : null;
		postObj.Territories.CareerVersion = (territoryVersionsObj.CareerVersion) ? territoryVersionsObj.CareerVersion : null;
		postObj.Territories.CertificationVersion = (territoryVersionsObj.CertificationVersion) ? territoryVersionsObj.CertificationVersion : null;
		postObj.Territories.CollegeVersion = (territoryVersionsObj.CollegeVersion) ? territoryVersionsObj.CollegeVersion : null;
		postObj.Territories.K12Version = (territoryVersionsObj.K12Version) ? territoryVersionsObj.K12Version : null;
		postObj.Territories.MilitaryVersion = (territoryVersionsObj.MilitaryVersion) ? territoryVersionsObj.MilitaryVersion : null;
		postObj.Territories.PreKVersion = (territoryVersionsObj.PreKVersion) ? territoryVersionsObj.PreKVersion : null;

		return postObj;
	}

	getMapSavePostDataFromGetUserMapTerritoryVersionsObj(territoryVersionsObj: any, mapOptionId: string): any {
		var postObj: any = {
			MapOptionId: mapOptionId,
			Territories: {
				CTEVersion: null,
				CareerVersion: null,
				CertificationVersion: null,
				CollegeVersion: null,
				K12Version: null,
				MilitaryVersion: null,
				PreKVersion: null
			}
		};

		postObj.Territories.CTEVersion = (territoryVersionsObj.CTEVersion) ? territoryVersionsObj.CTEVersion.Version : null;
		postObj.Territories.CareerVersion = (territoryVersionsObj.CareerVersion) ? territoryVersionsObj.CareerVersion.Version : null;
		postObj.Territories.CertificationVersion = (territoryVersionsObj.CertificationVersion) ? territoryVersionsObj.CertificationVersion.Version : null;
		postObj.Territories.CollegeVersion = (territoryVersionsObj.CollegeVersion) ? territoryVersionsObj.CollegeVersion.Version : null;
		postObj.Territories.K12Version = (territoryVersionsObj.K12Version) ? territoryVersionsObj.K12Version.Version : null;
		postObj.Territories.MilitaryVersion = (territoryVersionsObj.MilitaryVersion) ? territoryVersionsObj.MilitaryVersion.Version : null;
		postObj.Territories.PreKVersion = (territoryVersionsObj.PreKVersion) ? territoryVersionsObj.PreKVersion.Version : null;

		return postObj;
	}


	getMapSavePostDataFromGetUserMapResponse(getUserMapResponse: any): any {
		var postObj: any = {
			MapOptionId: getUserMapResponse.MapOptionId,
			Territories: {
				CTEVersion: null,
				CareerVersion: null,
				CertificationVersion: null,
				CollegeVersion: null,
				K12Version: null,
				MilitaryVersion: null,
				PreKVersion: null
			}
		};

		postObj.Territories.CTEVersion = (getUserMapResponse.CTEVersion) ? getUserMapResponse.CTEVersion.Version : null;
		postObj.Territories.CareerVersion = (getUserMapResponse.CareerVersion) ? getUserMapResponse.CareerVersion.Version : null;
		postObj.Territories.CertificationVersion = (getUserMapResponse.CertificationVersion) ? getUserMapResponse.CertificationVersion.Version : null;
		postObj.Territories.CollegeVersion = (getUserMapResponse.CollegeVersion) ? getUserMapResponse.CollegeVersion.Version : null;
		postObj.Territories.K12Version = (getUserMapResponse.K12Version) ? getUserMapResponse.K12Version.Version : null;
		postObj.Territories.MilitaryVersion = (getUserMapResponse.MilitaryVersion) ? getUserMapResponse.MilitaryVersion.Version : null;
		postObj.Territories.PreKVersion = (getUserMapResponse.PreKVersion) ? getUserMapResponse.PreKVersion.Version : null;

		return postObj;
	}

	ngOnInit() {}
}
